/**
 * Lead Pacing Report Renderer
 *
 * Marketing report showing company-wide and per-rooftop MTD leads vs goal,
 * with a visual progress bar (today / at-pace / gap), close-rate-vs-rolling
 * delta, units-progress tile, and NEW/USED gross variance % chips.
 *
 * Pacing math uses the 6-month rolling closing ratio (more stable predictor
 * than current MTD). Current MTD close is shown separately for context.
 */

import React from 'react';
import { PacingProgressBar, paceStatus, computeSegmentWidths } from '../../shared/PacingProgressBar';

export interface LeadPacingLocationRow {
  locationId: number;
  locationName: string;
  mtdLeads: number;
  mtdUnits: number;
  closingRatioPct: number | null;
  unitGoal: number;
  avgLeadsPerCalDay: number;
  projectedEomLeads: number;
  totalLeadsNeeded: number | null;       // computed off rolling close
  incrementalLeadsNeeded: number | null; // can be negative (ahead of pace)
  rollingClosingRatioPct: number | null;
  rollingMonthsWithData: number;
  newDeptGrossVariancePct: number | null;
  usedDeptGrossVariancePct: number | null;
}

export interface LeadPacingCompanyRow {
  mtdLeads: number;
  mtdUnits: number;
  closingRatioPct: number | null;
  unitGoal: number;
  avgLeadsPerCalDay: number;
  projectedEomLeads: number;
  totalLeadsNeeded: number | null;
  incrementalLeadsNeeded: number | null;
  rollingClosingRatioPct: number | null;
  rollingMonthsWithData: number;
  newDeptGrossVariancePct: number | null;
  usedDeptGrossVariancePct: number | null;
}

export interface LeadPacingReportData {
  reportMonth: {
    year: number;
    month: number;
    monthName: string;
    daysInMonth: number;
    daysElapsed: number;
  };
  company: LeadPacingCompanyRow;
  locations: LeadPacingLocationRow[];
}

const numFmt = new Intl.NumberFormat('en-US');
const fmtPct = (v: number | null, decimals = 1) =>
  v === null || v === undefined ? '—' : `${v.toFixed(decimals)}%`;
const fmtPctSigned = (v: number | null) => {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

function varianceColor(v: number | null): string {
  if (v === null) return 'text-gray-400';
  if (v >= 0) return 'text-green-700';
  if (v > -5) return 'text-amber-700';
  return 'text-rose-700';
}

// ──────────────────────────────────────────────────────────────────────────
// Tiles
// ──────────────────────────────────────────────────────────────────────────

const Tile: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</div>
    {children}
  </div>
);

const UnitsTile: React.FC<{ mtdUnits: number; unitGoal: number }> = ({ mtdUnits, unitGoal }) => {
  const pct = unitGoal > 0 ? Math.min(100, (mtdUnits / unitGoal) * 100) : 0;
  return (
    <Tile title="Units sold">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-gray-900">{numFmt.format(mtdUnits)}</span>
        <span className="text-sm text-gray-500">of {unitGoal > 0 ? numFmt.format(unitGoal) : '—'}</span>
      </div>
      {unitGoal > 0 && (
        <>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-gray-500 mt-1">{pct.toFixed(0)}% of monthly goal</div>
        </>
      )}
      {unitGoal === 0 && (
        <div className="text-xs text-gray-400 italic">No unit goal set for this month</div>
      )}
    </Tile>
  );
};

const CloseRateTile: React.FC<{
  closingRatioPct: number | null;
  rollingClosingRatioPct: number | null;
  rollingMonthsWithData: number;
}> = ({ closingRatioPct, rollingClosingRatioPct, rollingMonthsWithData }) => {
  const delta = (closingRatioPct !== null && rollingClosingRatioPct !== null)
    ? Math.round((closingRatioPct - rollingClosingRatioPct) * 10) / 10
    : null;
  let deltaPill: { label: string; cls: string } | null = null;
  if (delta !== null) {
    if (delta < 0) deltaPill = { label: `▼ ${Math.abs(delta).toFixed(1)} pts`, cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    else if (delta > 0) deltaPill = { label: `▲ ${delta.toFixed(1)} pts`, cls: 'bg-green-50 text-green-700 border-green-200' };
    else deltaPill = { label: '= 0 pts', cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  }
  return (
    <Tile title="Close rate this month">
      <div className="text-2xl font-bold text-gray-900 mb-2">{fmtPct(closingRatioPct)}</div>
      {deltaPill && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${deltaPill.cls}`}>
          {deltaPill.label}
        </span>
      )}
      <div className="text-xs text-gray-500 mt-2">
        6-mo rolling: {fmtPct(rollingClosingRatioPct)}{rollingMonthsWithData < 6 && rollingClosingRatioPct !== null ? ` (${rollingMonthsWithData} of 6 mo)` : ''}
      </div>
    </Tile>
  );
};

const GrossVarianceTile: React.FC<{
  newVar: number | null;
  usedVar: number | null;
}> = ({ newVar, usedVar }) => (
  <Tile title="Gross variance">
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-gray-700">New dept</span>
        <span className={`text-base font-semibold ${varianceColor(newVar)}`}>{fmtPctSigned(newVar)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-gray-700">Used dept</span>
        <span className={`text-base font-semibold ${varianceColor(usedVar)}`}>{fmtPctSigned(usedVar)}</span>
      </div>
    </div>
  </Tile>
);

// ──────────────────────────────────────────────────────────────────────────
// Card (used for both per-location rows and the company total)
// ──────────────────────────────────────────────────────────────────────────

interface PacingCardProps {
  title: string;
  subtitle: string;
  mtdLeads: number;
  mtdUnits: number;
  closingRatioPct: number | null;
  unitGoal: number;
  avgLeadsPerCalDay: number;
  projectedEomLeads: number;
  totalLeadsNeeded: number | null;
  incrementalLeadsNeeded: number | null;
  rollingClosingRatioPct: number | null;
  rollingMonthsWithData: number;
  newDeptGrossVariancePct: number | null;
  usedDeptGrossVariancePct: number | null;
  emphasis?: boolean;  // company card gets a slightly heavier border
}

const PacingCard: React.FC<PacingCardProps> = (p) => {
  const status = paceStatus(p.incrementalLeadsNeeded);
  const ahead = p.totalLeadsNeeded !== null && p.projectedEomLeads >= p.totalLeadsNeeded;
  const gap = p.totalLeadsNeeded !== null && !ahead ? p.totalLeadsNeeded - p.projectedEomLeads : 0;
  const atPace = Math.max(0, p.projectedEomLeads - p.mtdLeads);
  const widths = computeSegmentWidths(p.mtdLeads, atPace, gap);

  return (
    <div className={`rounded-xl p-5 ${p.emphasis ? 'bg-pink-50 border-2 border-pink-200' : 'bg-gray-50 border border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h3 className="text-base font-bold text-gray-900">{p.title}</h3>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${status.pillClass}`}>
          {status.label}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-3">{p.subtitle}</p>

      {/* Progress bar */}
      <PacingProgressBar
        mtdLeads={p.mtdLeads}
        atPace={atPace}
        gap={gap}
      />

      {/* Scale row — tick positions match the bumped segment widths so labels
          stay aligned with their segment edges even when min-width kicks in. */}
      <div className="relative h-5 text-[11px] text-gray-500 mt-1">
        <span className="absolute left-0">0</span>
        {p.mtdLeads > 0 && widths.todayCum < 80 && (
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${widths.todayCum}%` }}
          >
            {numFmt.format(p.mtdLeads)} today
          </span>
        )}
        <span className="absolute right-0 whitespace-nowrap text-right">
          {numFmt.format(p.projectedEomLeads)} at pace
          {p.totalLeadsNeeded !== null && !ahead && `, ${numFmt.format(p.totalLeadsNeeded)} needed`}
          {ahead && p.totalLeadsNeeded !== null && ` (${numFmt.format(p.totalLeadsNeeded)} needed)`}
        </span>
      </div>

      {/* Caption — close rate shown here is the one DRIVING the pacing math
          (6-mo rolling), not MTD. MTD is in the "Close rate this month" tile.
          Falls back to MTD only when no rolling history exists. */}
      <p className="text-xs text-gray-600 mt-2 mb-4">
        At {p.avgLeadsPerCalDay.toFixed(1)} leads/cal-day · {
          p.rollingClosingRatioPct !== null
            ? `${fmtPct(p.rollingClosingRatioPct)} 6-mo rolling close`
            : p.closingRatioPct !== null
              ? `${fmtPct(p.closingRatioPct)} MTD close (no rolling history)`
              : 'no close rate available'
        } · {p.unitGoal > 0 ? `${numFmt.format(p.unitGoal)}-unit goal` : 'no unit goal set'}
      </p>

      {/* Tile row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <UnitsTile mtdUnits={p.mtdUnits} unitGoal={p.unitGoal} />
        <CloseRateTile
          closingRatioPct={p.closingRatioPct}
          rollingClosingRatioPct={p.rollingClosingRatioPct}
          rollingMonthsWithData={p.rollingMonthsWithData}
        />
        <GrossVarianceTile
          newVar={p.newDeptGrossVariancePct}
          usedVar={p.usedDeptGrossVariancePct}
        />
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Top-level renderer
// ──────────────────────────────────────────────────────────────────────────

export const LeadPacingReport: React.FC<{ data: LeadPacingReportData }> = ({ data }) => {
  const { reportMonth, company, locations } = data;
  const periodLabel = `${reportMonth.monthName} ${reportMonth.year} · day ${reportMonth.daysElapsed} of ${reportMonth.daysInMonth}`;

  return (
    <div className="space-y-4">
      <PacingCard
        title="Company Total"
        subtitle={`Lead pacing — ${periodLabel}`}
        mtdLeads={company.mtdLeads}
        mtdUnits={company.mtdUnits}
        closingRatioPct={company.closingRatioPct}
        unitGoal={company.unitGoal}
        avgLeadsPerCalDay={company.avgLeadsPerCalDay}
        projectedEomLeads={company.projectedEomLeads}
        totalLeadsNeeded={company.totalLeadsNeeded}
        incrementalLeadsNeeded={company.incrementalLeadsNeeded}
        rollingClosingRatioPct={company.rollingClosingRatioPct}
        rollingMonthsWithData={company.rollingMonthsWithData}
        newDeptGrossVariancePct={company.newDeptGrossVariancePct}
        usedDeptGrossVariancePct={company.usedDeptGrossVariancePct}
        emphasis
      />

      <div className="pt-2">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">By Rooftop</h3>
        <div className="space-y-3">
          {locations.map(loc => (
            <PacingCard
              key={loc.locationId}
              title={loc.locationName}
              subtitle={`Lead pacing — day ${reportMonth.daysElapsed} of ${reportMonth.daysInMonth}`}
              mtdLeads={loc.mtdLeads}
              mtdUnits={loc.mtdUnits}
              closingRatioPct={loc.closingRatioPct}
              unitGoal={loc.unitGoal}
              avgLeadsPerCalDay={loc.avgLeadsPerCalDay}
              projectedEomLeads={loc.projectedEomLeads}
              totalLeadsNeeded={loc.totalLeadsNeeded}
              incrementalLeadsNeeded={loc.incrementalLeadsNeeded}
              rollingClosingRatioPct={loc.rollingClosingRatioPct}
              rollingMonthsWithData={loc.rollingMonthsWithData}
              newDeptGrossVariancePct={loc.newDeptGrossVariancePct}
              usedDeptGrossVariancePct={loc.usedDeptGrossVariancePct}
            />
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Pacing uses the 6-month rolling closing ratio (a more stable predictor than current MTD). Current MTD close rate is shown for context, with the delta vs rolling on each card. Gross variance % is the MTD trending vs the projection target — same calculation as the CEO Daily Brief, Section 4.
      </p>
    </div>
  );
};
